import React from "react";
import Reveal from "./Reveal";
import { ArrowUpRight } from "lucide-react";
import CaseImageCarousel from "./CaseImageCarousel";

/**
 * Each study supports an `images: [{ src, objectPosition?, bg?, alt? }, ...]`
 * Add more entries to the array to extend the carousel.
 */
const studies = [
  {
    id: "london-lighting",
    no: "01",
    category: "Business",
    title: "London Lighting",
    summary:
      "Minimal social presence, inconsistent output. Rebuilt with high impact brand story reels, daily posting and search optimised positioning.",
    images: [
      {
        src: "https://customer-assets.emergentagent.com/job_stand-out-branding/artifacts/it3um80z_IMG_7882.PNG",
        objectPosition: "top",
        bg: "#0b0b0d",
        alt: "London Lighting Instagram profile",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_stand-out-branding/artifacts/guwmi5b1_LL%20turns%204.MOV",
        bg: "#0b0b0d",
        alt: "London Lighting brand video",
        isVideo: true,
      },
    ],
    metrics: [
      { value: "+95%", label: "Instagram followers (6K → 11.7K)" },
      { value: "11.9K", label: "TikTok followers + 504K likes" },
      { value: "1.2M", label: "Monthly Instagram views" },
    ],
    layout: "left",
  },
  {
    id: "ukfilmwork",
    no: "02",
    category: "Community",
    title: "UkFilmWork",
    summary:
      "Authority without monetisation. Brokered a partnership deal, scaled content quality, produced a 600 person live event that converted attention into revenue.",
    images: [
      {
        src: "https://customer-assets.emergentagent.com/job_stand-out-branding/artifacts/yyv7rd5b_IMG_7879.PNG",
        objectPosition: "top",
        bg: "#0b0b0d",
        alt: "UkFilmWork Instagram profile",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_stand-out-branding/artifacts/5xuo45r5_IMG_7881.jpeg",
        objectPosition: "center",
        bg: "#0b0b0d",
        alt: "UkFilmWork Instagram grid",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_stand-out-branding/artifacts/8fx6y4za_IMG_0621%202.jpg",
        objectPosition: "center",
        bg: "#0b0b0d",
        alt: "UkFilmWork team",
      },
    ],
    metrics: [
      { value: "38K → 53K", label: "Followers in 8 months" },
      { value: "1.6M", label: "Views in last 30 days" },
      { value: "5-fig", label: "Brand partnerships + event revenue" },
    ],
    layout: "right",
  },
  {
    id: "jordan-young",
    no: "03",
    category: "Personal Brand",
    title: "Jordan Young",
    summary:
      "Achievements weren't converting on socials. A multi platform compounding strategy turned authority into investor interest, speaking and direct revenue.",
    images: [
      {
        src: "https://customer-assets.emergentagent.com/job_stand-out-branding/artifacts/e6slxyv1_LLDN5597.JPG",
        objectPosition: "center",
        alt: "Jordan Young portrait",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_stand-out-branding/artifacts/ifpv1aa8_IMG_77EA43A1499E-1.jpeg",
        objectPosition: "top",
        bg: "#0b0b0d",
        alt: "Jordan Young growth analytics",
      },
    ],
    metrics: [
      { value: "0 → 1.2K", label: "YouTube subs in 1 month (monetised)" },
      { value: "2.8K → 12.6K", label: "Instagram in 6 months" },
      { value: "5-fig", label: "Direct funnel revenue" },
    ],
    layout: "left",
  },
];

const Card = ({ s }) => {
  const reverse = s.layout === "right";
  return (
    <article
      data-testid={`case-study-${s.id}`}
      className="grid grid-cols-12 gap-6 lg:gap-12 items-start mb-32 last:mb-0"
    >
      <Reveal
        className={`col-span-12 lg:col-span-5 ${
          reverse ? "lg:col-start-8 lg:order-2" : "lg:col-start-1 lg:order-1"
        }`}
      >
        <CaseImageCarousel images={s.images} testId={s.id} />
      </Reveal>

      <Reveal
        delay={140}
        className={`col-span-12 lg:col-span-6 ${
          reverse ? "lg:col-start-1 lg:order-1 lg:pr-8" : "lg:col-start-7 lg:order-2 lg:pl-4"
        }`}
      >
        <div className="flex items-center gap-4 mb-6">
          <span className="lt-num text-xl">{s.no}</span>
          <span className="eyebrow">{s.category}</span>
        </div>
        <h3
          className="font-display tracking-editorial text-[var(--lt-charcoal)] leading-[1] mb-8"
          style={{ fontSize: "clamp(2rem, 3.6vw, 3.4rem)", fontWeight: 600 }}
        >
          {s.title}
        </h3>
        <p className="text-[var(--lt-bark)] text-base lg:text-lg leading-relaxed mb-10">
          {s.summary}
        </p>

        <div className="grid grid-cols-1 gap-0 border-t border-[var(--lt-line)]">
          {s.metrics.map((m) => (
            <div
              key={m.label}
              className="flex items-baseline justify-between gap-6 py-5 border-b border-[var(--lt-line)]"
              data-testid={`case-metric-${s.id}-${m.label.split(" ")[0]}`}
            >
              <span
                className="font-display tracking-editorial text-[var(--lt-charcoal)]"
                style={{ fontSize: "1.8rem", fontWeight: 600 }}
              >
                {m.value}
              </span>
              <span className="text-xs text-[var(--lt-bark)] text-right max-w-[55%]">
                {m.label}
              </span>
            </div>
          ))}
        </div>
      </Reveal>
    </article>
  );
};

const CaseStudies = () => {
  return (
    <section
      id="work"
      data-testid="case-studies-section"
      className="py-28 lg:py-36 bg-[var(--lt-peace)]"
    >
      <div className="container mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 mb-20 lg:mb-28">
          <div className="col-span-12 lg:col-span-6">
            <Reveal>
              <p className="eyebrow mb-4">/ Selected Work</p>
              <h2
                className="font-display tracking-editorial text-[var(--lt-charcoal)] leading-[1.05]"
                style={{ fontSize: "clamp(2rem, 4.4vw, 4rem)", fontWeight: 600 }}
              >
                Results that <em className="italic text-[var(--lt-bark)]">compound</em>.
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end gap-6">
            <Reveal delay={160} className="flex items-center gap-4">
              <a
                href="/showcase"
                data-testid="case-studies-showcase-link"
                className="inline-flex items-center gap-2 text-[var(--lt-charcoal)] text-sm tracking-[0.18em] uppercase lt-link"
              >
                View Showcase <ArrowUpRight size={16} />
              </a>
            </Reveal>
          </div>
        </div>

        {studies.map((s) => (
          <Card key={s.id} s={s} />
        ))}
      </div>
    </section>
  );
};

export default CaseStudies;
